import React from 'react';
import type { EpicSession } from '../../../../shared/types/epics';
import { StatusBadge } from '../ui/StatusBadge';
import type { StatusTone } from '../ui/StatusBadge';
import { Button } from '../ui/Button';
import { agentEmoji } from '../../lib/agentEmoji';

// Shared agent-run row + formatting helpers. Single source of truth so a story's
// per-agent runs (RetryClusters) and a PR's per-agent runs (PRTrackingCard)
// render byte-identically — same columns, order, time format, and styling.

export const TH = 'px-4 py-2 text-2xs uppercase tracking-wide text-slate-500 font-semibold text-left';

export function formatTime(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

/**
 * FORGE-378: date + time for an agent run's start. Runs on an epic span days
 * (epic #136: Sep 18 → Oct 3), so a bare "18:03:57" was ambiguous. Shows the
 * month/day (and the year when it isn't the current one), local 24h time.
 */
export function formatStarted(iso: string, now: Date = new Date()): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString([], {
    month: 'short', day: 'numeric',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
  return `${date} ${formatTime(iso)}`;
}

export function formatDuration(ms: number | null, startedAt: string, isRunning: boolean): string {
  const totalMs = ms ?? (isRunning ? Date.now() - new Date(startedAt).getTime() : 0);
  if (!Number.isFinite(totalMs) || totalMs <= 0) return '—';
  const totalSec = Math.floor(totalMs / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function formatCost(usd: number): string {
  if (!usd || usd === 0) return '$0';
  if (usd < 0.01) return `$${usd.toFixed(6)}`;
  return `$${usd.toFixed(4)}`;
}

export function trimModel(modelId: string): string {
  if (!modelId) return '—';
  return modelId.replace(/^claude-/, '').replace(/-\d{8}$/, '');
}

export function outcomeTone(outcome: string | null): StatusTone {
  if (!outcome) return 'neutral';
  const o = outcome.toLowerCase();
  // FORGE-220: force_complete is a give-up / partial exit — amber, not green.
  if (o.includes('force') || o === 'terminated') return 'warning';
  if (o.includes('complete') || o === 'task_complete') return 'success';
  if (o.includes('fail') || o === 'error' || o === 'crashed') return 'danger';
  if (o.includes('rate') || o.includes('budget') || o.includes('progress') || o.includes('deadline')) return 'warning';
  return 'neutral';
}

export const SessionRow: React.FC<{
  session: EpicSession;
  onOpenTranscript: (agentType: string, taskArn?: string | null) => void;
  /** When false, omit the agent-name cell — used inside a retry cluster where
   *  the parent row already labels the agent. */
  showAgent?: boolean;
}> = ({ session, onOpenTranscript, showAgent = true }) => (
  <tr className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60">
    {showAgent && (
      <td className="px-4 py-2 font-medium text-slate-900">
        <span aria-hidden="true">{agentEmoji(session.agentType)}</span> {session.agentType}
      </td>
    )}
    <td className="px-4 py-2">
      {session.isRunning ? (
        <StatusBadge tone="inprogress" dot>
          running
        </StatusBadge>
      ) : (
        <StatusBadge tone={outcomeTone(session.outcome)}>
          {session.outcome ?? 'finished'}
        </StatusBadge>
      )}
    </td>
    <td
      className="px-4 py-2 tabular-nums font-mono text-xs text-slate-500 whitespace-nowrap"
      title={session.startedAt ? new Date(session.startedAt).toString() : undefined}
    >
      {formatStarted(session.startedAt)}
    </td>
    <td className="px-4 py-2 tabular-nums text-slate-700">
      {formatDuration(session.durationMs, session.startedAt, session.isRunning)}
    </td>
    <td className="px-4 py-2 text-xs text-slate-700" title={session.modelId}>
      {trimModel(session.modelId)}
    </td>
    <td className="px-4 py-2 tabular-nums font-mono text-slate-700">
      {formatCost(session.costUsd)}
    </td>
    <td className="px-4 py-2 tabular-nums text-slate-700">
      {session.productiveIterations}/{session.iterations}
    </td>
    <td className="px-4 py-2 text-right">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onOpenTranscript(session.agentType, session.taskArn)}
      >
        Transcript
      </Button>
    </td>
  </tr>
);
